/**
 * Check if a user has admin or superadmin permissions
 * @param {Object} session - User session object
 * @param {boolean} allowGracefulFail - If true, returns false instead of warning when settings missing
 * @returns {boolean} - True if user has admin privileges
 */
export function isAdmin(session, allowGracefulFail = false) {
  // Check if user exists
  if (!session?.user?.id) {
    return false;
  }

  // If user settings are missing, handle gracefully or warn
  if (!session?.user?.user_settings) {
    if (!allowGracefulFail) {
      console.warn(
        "⚠️ isAdmin: User settings missing from session, admin status unknown"
      );
    }
    return false;
  }

  const userRole = session.user.user_settings.user_role;
  const isAdminUser = userRole === "admin" || userRole === "superadmin";

  return isAdminUser;
}

/**
 * Check if a user has superadmin permissions specifically
 * @param {Object} session - User session object
 * @param {boolean} allowGracefulFail - If true, returns false instead of warning when settings missing
 * @returns {boolean} - True if user has superadmin privileges
 */
export function isSuperAdmin(session, allowGracefulFail = false) {
  // Check if user exists
  if (!session?.user?.id) {
    return false;
  }

  // If user settings are missing, handle gracefully or warn
  if (!session?.user?.user_settings) {
    if (!allowGracefulFail) {
      console.warn(
        "⚠️ isSuperAdmin: User settings missing from session, admin status unknown"
      );
    }
    return false;
  }

  const userRole = session.user.user_settings.user_role;
  const isSuperAdminUser = userRole === "superadmin";

  return isSuperAdminUser;
}

/**
 * Async version that can fetch user settings if missing
 * @param {Object} session - User session object
 * @param {Function} refreshSession - Function to refresh session with user settings
 * @returns {Promise<boolean>} - True if user has admin privileges
 */
export async function isAdminAsync(session, refreshSession) {
  if (!session?.user?.id) {
    return false;
  }

  // If user settings are missing, try to refresh session
  if (!session?.user?.user_settings && refreshSession) {
    console.log(
      "🔄 isAdminAsync: User settings missing, refreshing session..."
    );
    try {
      const refreshResult = await refreshSession();
      if (refreshResult.success) {
        // Get the updated session from the store
        const store = await import("@/store/useStore");
        const updatedSession = store.default.getState().session;
        return isAdmin(updatedSession, true);
      }
    } catch (error) {
      console.error("❌ isAdminAsync: Failed to refresh session:", error);
      return false;
    }
  }

  return isAdmin(session, true);
}

/**
 * Async version for superadmin check that can fetch user settings if missing
 * @param {Object} session - User session object
 * @param {Function} refreshSession - Function to refresh session with user settings
 * @returns {Promise<boolean>} - True if user has superadmin privileges
 */
export async function isSuperAdminAsync(session, refreshSession) {
  if (!session?.user?.id) {
    return false;
  }

  // If user settings are missing, try to refresh session
  if (!session?.user?.user_settings && refreshSession) {
    console.log(
      "🔄 isSuperAdminAsync: User settings missing, refreshing session..."
    );
    try {
      const refreshResult = await refreshSession();
      if (refreshResult.success) {
        // Get the updated session from the store
        const store = await import("@/store/useStore");
        const updatedSession = store.default.getState().session;
        return isSuperAdmin(updatedSession, true);
      }
    } catch (error) {
      console.error("❌ isSuperAdminAsync: Failed to refresh session:", error);
      return false;
    }
  }

  return isSuperAdmin(session, true);
}

/**
 * Safely perform a sign out operation with proper error handling
 * @param {Function} signOutFunction - The sign out function from the auth store
 * @returns {Promise<boolean>} - True if sign out was successful
 */
export async function safeSignOut(signOutFunction) {
  try {
    console.log("🚪 Starting safe sign out");
    const result = await signOutFunction();

    if (result.success) {
      console.log("✅ Safe sign out successful");
      return true;
    } else {
      console.warn("⚠️ Sign out completed with warnings:", result.error);
      return true; // Still consider it successful for UX purposes
    }
  } catch (error) {
    console.error("❌ Safe sign out error:", error);
    // Even if there's an error, the enhanced signOut should have cleared local state
    return true;
  }
}

/**
 * Safely perform an authentication operation with session validation
 * @param {Function} authOperation - The auth operation to perform
 * @param {Function} validateSession - Function to validate current session
 * @param {Object} options - Options for the operation
 * @returns {Promise<Object>} - Result of the operation
 */
export async function safeAuthOperation(
  authOperation,
  validateSession,
  options = {}
) {
  try {
    // Validate session first if required
    if (options.requireValidSession && validateSession) {
      console.log("🔍 Validating session before auth operation");
      const validationResult = await validateSession();

      if (!validationResult.valid) {
        console.log("❌ Session invalid, cannot proceed with auth operation");
        return {
          success: false,
          error: "Session invalid",
          requiresReauth: true,
        };
      }
    }

    // Perform the operation
    const result = await authOperation();
    return result;
  } catch (error) {
    console.error("❌ Safe auth operation error:", error);
    return {
      success: false,
      error: error.message || "Authentication operation failed",
    };
  }
}

/**
 * Check if a session is likely to be expired soon
 * @param {Object} session - User session object
 * @param {number} bufferMinutes - Minutes before expiry to consider "soon" (default: 5)
 * @returns {boolean} - True if session will expire soon
 */
export function isSessionExpiringSoon(session, bufferMinutes = 5) {
  if (!session?.expires_at) {
    return false;
  }

  const expiryTime = new Date(session.expires_at).getTime();
  const currentTime = Date.now();
  const bufferTime = bufferMinutes * 60 * 1000; // Convert to milliseconds

  return expiryTime - currentTime <= bufferTime;
}

/**
 * Check if session has valid user settings
 * @param {Object} session - User session object
 * @returns {boolean} - True if session has user settings
 */
export function hasValidUserSettings(session) {
  return !!(session?.user?.id && session?.user?.user_settings);
}

/**
 * Get a user-friendly error message for authentication errors
 * @param {Error|string} error - The error to process
 * @returns {string} - User-friendly error message
 */
export function getFriendlyAuthError(error) {
  const errorMessage =
    typeof error === "string" ? error : error?.message || "Unknown error";

  if (errorMessage.includes("Invalid login credentials")) {
    return "Invalid email or password. Please try again.";
  }

  if (errorMessage.includes("Email not confirmed")) {
    return "Please check your email and click the confirmation link.";
  }

  if (
    errorMessage.includes("Session invalid") ||
    errorMessage.includes("JWT expired")
  ) {
    return "Your session has expired. Please sign in again.";
  }

  if (
    errorMessage.includes("Network error") ||
    errorMessage.includes("fetch")
  ) {
    return "Network error. Please check your connection and try again.";
  }

  // For development, show the actual error. In production, show generic message
  if (process.env.NODE_ENV === "development") {
    return errorMessage;
  }

  return "An error occurred. Please try again.";
}

/**
 * Utility to wait for session to be ready with user settings
 * @param {Function} getSession - Function to get current session
 * @param {Function} refreshSession - Function to refresh session
 * @param {number} maxWaitTime - Maximum time to wait in milliseconds (default: 10000)
 * @returns {Promise<Object|null>} - Session with user settings or null
 */
export async function waitForValidSession(
  getSession,
  refreshSession,
  maxWaitTime = 10000
) {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitTime) {
    const session = getSession();

    if (hasValidUserSettings(session)) {
      return session;
    }

    if (session?.user?.id && !session.user.user_settings) {
      // Try to refresh session to get user settings
      try {
        const refreshResult = await refreshSession();
        if (refreshResult.success) {
          const updatedSession = getSession();
          if (hasValidUserSettings(updatedSession)) {
            return updatedSession;
          }
        }
      } catch (error) {
        console.error("❌ waitForValidSession: Refresh failed:", error);
      }
    }

    // Wait a bit before trying again
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  console.warn("⚠️ waitForValidSession: Timeout waiting for valid session");
  return null;
}
